import type { SyncBackBody } from "@/shared/types/lead-api-types"

/**
 * "Rentrée des classes" contest run with Animalis in September 2026 (DLM-1775).
 *
 * Users redirected from the Animalis landing page see a reminder banner for the
 * duration of the campaign. The banner links to the sweepstake T&C PDF, hosted
 * publicly by DLM-1756 and also referenced from the Animalis landing page
 * (dalmaTeam/landing-pages, DLM-1757).
 */
export const ANIMALIS_CONTEST_TC_URL =
  "https://editique.dalma.co/2026_09_01_Reglement_jeu_rentree_des_classes.pdf"

/**
 * localStorage key for the QA-only preview override (DLM-1775). When set (on
 * non-production only), it forces the campaign window "active" so reviewers can
 * see the banner outside September. It never bypasses the Animalis-traffic
 * requirement. See `use-animalis-contest-preview`.
 */
export const ANIMALIS_CONTEST_PREVIEW_STORAGE_KEY = "animalis_contest_preview"

// Campaign window: 2026-09-01 → 2026-09-30 inclusive, anchored to Europe/Paris.
// Fixed UTC instants so the boundaries are identical for every user regardless of
// their browser timezone. Paris is CEST (+02:00) across the whole window — it ends
// well before the 2026-10-25 DST switch — hence the -2h offset from midnight.
const CAMPAIGN_START = new Date("2026-08-31T22:00:00Z") // 2026-09-01 00:00 Europe/Paris
const CAMPAIGN_END_EXCLUSIVE = new Date("2026-09-30T22:00:00Z") // 2026-10-01 00:00 Europe/Paris

/**
 * Whether the campaign is running right now. The display depends only on the
 * current date, never on when the quote was created — a September quote resumed
 * in October must not show the banner.
 */
export const isAnimalisContestActive = (now: Date = new Date()): boolean =>
  now >= CAMPAIGN_START && now < CAMPAIGN_END_EXCLUSIVE

/**
 * The Animalis landing page tags the leads it creates with
 * `marketing.referrer_type = "animalis"` (dalmaTeam/landing-pages, create-lead
 * service). This is the only traffic that should see the banner — it is
 * distinct from the user-declared "partners → animalis" attribution answer,
 * which stores `referrer_type = "partners"`.
 */
const isAnimalisLandingTraffic = (quote: Partial<SyncBackBody> | undefined): boolean =>
  quote?.marketing?.referrer_type === "animalis"

/**
 * Whether the Animalis contest banner should be displayed for the current quote
 * and date. Callers own the precedence rule: if a promo code / RAF banner
 * already applies, it wins and this banner is not shown.
 *
 * `forceActiveWindow` is the QA-only escape hatch (non-production): it forces the
 * date gate open so reviewers can preview the banner outside September. It does
 * NOT bypass the Animalis-traffic requirement — a real Animalis quote is still
 * needed.
 */
export const isAnimalisContestBannerVisible = (
  quote: Partial<SyncBackBody> | undefined,
  now: Date = new Date(),
  { forceActiveWindow = false }: { forceActiveWindow?: boolean } = {}
): boolean =>
  isAnimalisLandingTraffic(quote) && (forceActiveWindow || isAnimalisContestActive(now))
